import { Component, type ReactNode } from "react";
import { Alert, Button, Stack, Text } from "@mantine/core";
import { useLocation } from "react-router-dom";

interface BoundaryProps {
    children: ReactNode;
    /** When this changes, a boundary that has caught an error tries rendering its children again */
    resetKey: string;
}

interface BoundaryState {
    hasError: boolean;
}

class Boundary extends Component<BoundaryProps, BoundaryState> {
    state: BoundaryState = { hasError: false };

    static getDerivedStateFromError(): BoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error) {
        console.error("UI rendering failed", error);
    }

    componentDidUpdate(prevProps: BoundaryProps) {
        if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ hasError: false });
        }
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            <Alert color="red" title="Något gick fel" role="alert">
                <Stack gap="sm">
                    <Text>
                        En del av HittaPodd kunde inte visas. Om du redan lyssnar kan uppspelningen fortsätta. Prova att
                        gå vidare med navigationen eller försök igen.
                    </Text>
                    <div>
                        <Button size="sm" variant="light" onClick={() => this.setState({ hasError: false })}>
                            Försök igen
                        </Button>
                    </div>
                </Stack>
            </Alert>
        );
    }
}

/**
 * Contains UI failures and retries automatically after navigation.
 * Resets in place rather than remounting its children, so the shell, Suspense boundaries
 * and view transitions survive navigation.
 */
export const ErrorBoundary = ({ children }: Pick<BoundaryProps, "children">) => {
    const location = useLocation();

    return <Boundary resetKey={`${location.pathname}${location.search}`}>{children}</Boundary>;
};
